import { createNavigation } from 'next-intl/navigation';
import { defineRouting } from 'next-intl/routing';

export const routing = defineRouting({
  // A list of all locales that are supported
  locales: ['sk', 'en'],

  // Used when no locale matches
  defaultLocale: 'sk',

  // `localeDetection: false` disables accept-language/cookie-based locale
  // detection so the URL is the sole source of truth for locale routing.
  // In next-intl v4 (#10057 dependabot bump), `localeDetection: false` no
  // longer implies "don't set a cookie either" — a separate `localeCookie`
  // option was introduced, and setting it explicitly to `false` here keeps
  // the original v3 behavior (next-intl set NO `NEXT_LOCALE` cookie).
  // Preserving "no cookie" is deliberate, not incidental: this app has a
  // dedicated `(consent-manger)` route group for cookie consent, so a new
  // cookie appearing as a side effect of a dependency bump — outside that
  // consent flow — would be a real compliance regression, not just a
  // behavior change. See next-intl's v4.0 release notes ("GDPR compliance").
  localeDetection: false,
  localeCookie: false,

  pathnames: {
    '/': '/',
    '/about': {
      en: '/about',
      sk: '/o-nas',
    },
    '/platform-neutrality': {
      en: '/platform-neutrality',
      sk: '/neutralita-platformy',
    },
    '/why-lexomat': {
      en: '/why-lexomat',
      sk: '/preco-lexomat',
    },
    '/chunk-viewer': {
      en: '/chunk-viewer',
      sk: '/chunk-viewer',
    },
    '/contact': {
      en: '/contact',
      sk: '/kontakt',
    },
    '/chat': {
      en: '/chat',
      sk: '/chat',
    },
    '/chat/[sessionId]': {
      en: '/chat/[sessionId]',
      sk: '/chat/[sessionId]',
    },
    '/chat/share/[sessionId]': {
      en: '/chat/share/[sessionId]',
      sk: '/chat/share/[sessionId]',
    },
    '/chats': {
      en: '/chats',
      sk: '/chats',
    },
    '/feedback': {
      en: '/feedback',
      sk: '/spatne',
    },
    '/privacy-policy': {
      en: '/privacy-policy',
      sk: '/zasady-ochrany-osobnych-udajov',
    },
    '/research': {
      en: '/research',
      sk: '/resers',
    },
    '/research/[sessionId]': {
      en: '/research/[sessionId]',
      sk: '/resers/[sessionId]',
    },
    '/search': {
      en: '/search',
      sk: '/vyhladavanie',
    },
    // The morning brief (#9683) — the advocate's own surface for the compiled day.
    '/today': {
      en: '/today',
      sk: '/dnes',
    },
    '/search/[sessionId]': {
      en: '/search/[sessionId]',
      sk: '/vyhladavanie/[sessionId]',
    },
    '/results': {
      en: '/results',
      sk: '/vysledky',
    },
    '/results/[sessionId]': {
      en: '/results/[sessionId]',
      sk: '/vysledky/[sessionId]',
    },
    '/terms-of-use': {
      en: '/terms-of-use',
      sk: '/vseobecne-obchodne-podmienky',
    },
    '/processing-terms': {
      en: '/processing-terms',
      sk: '/spracovatelske-podmienky',
    },
    // Cookie policy text page (GDPR / ePrivacy). Identical slugs in both
    // locales so the consent UI can deep-link without locale branching.
    '/cookies': {
      en: '/cookies',
      sk: '/cookies',
    },
    // Advocate-data-processing notice — distinct from /privacy-policy because
    // it focuses on the GDPR Art. 13/14 rights of data subjects whose data
    // flows through the advocate profile pipeline (SAK-aware disclosures).
    '/gdpr': {
      en: '/gdpr',
      sk: '/gdpr',
    },
    // GDPR Art. 28(4) + Metodické usmernenie SAK k Uzneseniu č. 21/14/2025
    // čl. 6 ods. 2 písm. d) — public sub-processor disclosure. URL is
    // identical in both locales because it's referenced verbatim from the
    // DPA template (Čl. 6.2).
    '/dpa/sub-processors': {
      en: '/dpa/sub-processors',
      sk: '/dpa/sub-processors',
    },
    // Handle SK/ZZ as a special case that doesn't get translated
    '/document/[...segments]': {
      en: '/document/[...segments]',
      sk: '/dokument/[...segments]',
    },
    '/pricing': {
      en: '/pricing',
      sk: '/cennik',
    },
    '/manage': {
      en: '/manage',
      sk: '/spravovat',
    },
    '/cancelling-subscription': {
      en: '/cancelling-subscription',
      sk: '/zrusenie-predplatneho',
    },
    // Newsletter double opt-in landing page (#12323) — the confirmation
    // email links here with the token in the fragment (`#token=`), which
    // never reaches a server.
    '/confirm-subscription': {
      en: '/confirm-subscription',
      sk: '/potvrdenie-odberu',
    },
    '/how-we-rank': {
      en: '/how-we-rank',
      sk: '/zoradenie',
    },
    '/user-profile': {
      en: '/user-profile',
      sk: '/profil-pouzivatela',
    },
    '/user-profile/history': {
      en: '/user-profile/history',
      sk: '/profil-pouzivatela/historia',
    },
    '/user-profile/subscription': {
      en: '/user-profile/subscription',
      sk: '/profil-pouzivatela/predplatne',
    },
    // Professional (Profil+) subscription page. Served at the literal path on
    // both locales (it is NOT localized like the personal subscription slug) —
    // registered here so the type-safe Link accepts it; SubscriptionTab + the
    // Stripe return URL already use this literal path. Do NOT add a SK slug
    // (would break those existing links).
    '/user-profile/professional/subscription': {
      en: '/user-profile/professional/subscription',
      sk: '/user-profile/professional/subscription',
    },
    '/user-profile/invoice': {
      en: '/user-profile/invoice',
      sk: '/profil-pouzivatela/faktura',
    },
    '/user-profile/billing': {
      en: '/user-profile/billing',
      sk: '/profil-pouzivatela/fakturacne-udaje',
    },
    '/user-profile/user-type': {
      en: '/user-profile/user-type',
      sk: '/profil-pouzivatela/typ-pouzivatela',
    },
    '/user-profile/appearance': {
      en: '/user-profile/appearance',
      sk: '/profil-pouzivatela/vzhlad',
    },
    '/user-profile/email-preferences': {
      en: '/user-profile/email-preferences',
      sk: '/profil-pouzivatela/emailove-preferencie',
    },
    '/user-profile/preferences': {
      en: '/user-profile/preferences',
      sk: '/profil-pouzivatela/preferencie',
    },
    '/user-profile/organizations': {
      en: '/user-profile/organizations',
      sk: '/profil-pouzivatela/organizacie',
    },
    '/user-profile/security': {
      en: '/user-profile/security',
      sk: '/profil-pouzivatela/bezpecnost',
    },
    '/user-profile/agreements': {
      en: '/user-profile/agreements',
      sk: '/profil-pouzivatela/zmluvy',
    },
    // MCP AI-connection settings (#10556) — the spis-scope attestation page
    // (R2(A): "a page in the advocate's own application, e.g. in the
    // AI-connection settings"). Sibling of /user-profile/agreements.
    '/user-profile/ai-connections': {
      en: '/user-profile/ai-connections',
      sk: '/profil-pouzivatela/ai-pripojenia',
    },
    '/user-profile/usage': {
      en: '/user-profile/usage',
      sk: '/profil-pouzivatela/pouzivanie',
    },
    '/research/share/[sessionId]': {
      en: '/research/share/[sessionId]',
      sk: '/resers/zdieľať/[sessionId]',
    },
    // EN → /sign-in / /sign-up; SK keeps the canonical folder names. The
    // App Router resolves the canonical pathname (`/prihlasenie`,
    // `/registracia`) regardless of which locale URL the user visits — the
    // route folders stay where they are, only the user-facing URL changes.
    '/prihlasenie': {
      en: '/sign-in',
      sk: '/prihlasenie',
    },
    '/registracia': {
      en: '/sign-up',
      sk: '/registracia',
    },
    '/professional-directory': {
      en: '/professional-directory',
      sk: '/najdite-odbornika',
    },
    '/professional-directory/claim/[id]': {
      en: '/professional-directory/claim/[id]',
      sk: '/najdite-odbornika/narokovat/[id]',
    },
    '/professional-directory/claim/verify': {
      en: '/professional-directory/claim/verify',
      sk: '/najdite-odbornika/narokovat/verify',
    },
    '/professional-directory/dashboard': {
      en: '/professional-directory/dashboard',
      sk: '/najdite-odbornika/dashboard',
    },
    '/for-professionals': {
      en: '/for-professionals',
      sk: '/pre-profesionalov',
    },
    // Advocate product landing page (#7230) — step 1 of the DPA activation
    // funnel. Dark-launched: the page 404s until ADVOCATE_LANDING_ENABLED
    // flips on (launch PR also repoints the /pre-advokata alias here).
    '/for-lawyers': {
      en: '/for-lawyers',
      sk: '/pre-advokatov',
    },
    // Spisy Trial for 100 redeem/self-service page (#8894). Same slug in
    // both locales: "Spisy Zdarma" is the campaign's own name, not a
    // translatable phrase.
    '/spisy-zdarma': {
      en: '/spisy-zdarma',
      sk: '/spisy-zdarma',
    },
    // Public documentation page for the MCP connector (#9552 family —
    // docs/mcp-directory-submission.md §2/§3). Same slug in both locales
    // deliberately: "mcp" is the protocol name (Model Context Protocol), not
    // a translatable word, so there is no Slovak equivalent to localize to.
    '/mcp': {
      en: '/mcp',
      sk: '/mcp',
    },
    // Public documentation hub (#10068). Same slug in both locales
    // deliberately: "doc" is the section name, not a translatable word.
    '/doc': {
      en: '/doc',
      sk: '/doc',
    },
    '/doc/[slug]': {
      en: '/doc/[slug]',
      sk: '/doc/[slug]',
    },
    // Canonical advocate directory (Maros SEO scheme, 2026-07-21): one
    // canonical URL per locale — SK '/advokati', EN '/advocates' (the English
    // word). The internal route folder stays `advokati/`; next-intl maps the
    // EN locale to '/advocates'. Every other listing path (vyhladat-advokata,
    // pravnici, najst-pravnika, najdite-odbornika, regulovane-profesie, lawyers,
    // professional-directory) 301-redirects here — see next.config.mjs.
    '/advokati': {
      en: '/advocates',
      sk: '/advokati',
    },
    '/advokati/[id]': {
      en: '/advocates/[id]',
      sk: '/advokati/[id]',
    },
    // Advocate blog — public, SEO-indexed per-lawyer blog surface
    // (Phase 5 of Advocate Blog v1). SK stays under /advokati, EN under
    // /advocates — same tree, per-locale slug.
    '/advokati/[id]/blog': {
      en: '/advocates/[id]/blog',
      sk: '/advokati/[id]/blog',
    },
    '/advokati/[id]/blog/[slug]': {
      en: '/advocates/[id]/blog/[slug]',
      sk: '/advokati/[id]/blog/[slug]',
    },
    // SAK law-office (advokátske kancelárie) public directory (#8021 v1).
    // Deliberately NOT '/kancelarie' — one letter away from the advocate's
    // private workspace '/kancelaria', a real mis-navigation risk. The full
    // keyword slug is also the exact Slovak search term.
    '/law-offices': {
      en: '/law-offices',
      sk: '/advokatske-kancelarie',
    },
    '/law-offices/[id]': {
      en: '/law-offices/[id]',
      sk: '/advokatske-kancelarie/[id]',
    },
    // City-targeted SEO landing pages (Chunk E of SEO follow-ups).
    // Same slug in both locales — the surface is branded "mesto"
    // (Slovak for "city") and the city identifier is ASCII-lowercased
    // (e.g. "bratislava", "banska-bystrica") for portability.
    '/mesto/[city]': {
      en: '/mesto/[city]',
      sk: '/mesto/[city]',
    },
    // City × practice-area programmatic landing pages (issue #2972 Phase 3).
    // Same identical-slug convention as the two hubs they sit between.
    '/mesto/[city]/[category]': {
      en: '/mesto/[city]/[category]',
      sk: '/mesto/[city]/[category]',
    },
    // Practice-area SEO landing pages. Same slug in both locales, same
    // reasoning as /mesto above. These existed in the `Pathname` type union
    // below and as real routes, but NOT here — and `createNavigation(routing)`
    // derives Link's accepted hrefs from THIS object, not from that union. So
    // `<Link href={{ pathname: '/oblast-prava/[category]', ... }}>` failed to
    // type-check, and the same-class runtime failure the SEO links hit applies:
    // a pathname absent here cannot be resolved.
    '/oblast-prava': {
      en: '/oblast-prava',
      sk: '/oblast-prava',
    },
    '/oblast-prava/[category]': {
      en: '/oblast-prava/[category]',
      sk: '/oblast-prava/[category]',
    },
    // SEO target-query landing pages (issue #2972 Phase 1 + #2974 content
    // structure). SK slugs are the exact target queries from GSC; EN slugs
    // are genuine English equivalents, not literal transliterations —
    // mirrors the `/advokati` → `/advocates`-style per-locale split above
    // rather than the identical-slug pattern used for untranslatable brand
    // terms (e.g. `/mesto` / `/oblast-prava`).
    '/pravna-resers-online': {
      en: '/legal-research-online',
      sk: '/pravna-resers-online',
    },
    '/ai-pre-pravnikov': {
      en: '/ai-for-lawyers',
      sk: '/ai-pre-pravnikov',
    },
    '/vyhladavanie-judikatov': {
      en: '/case-law-search',
      sk: '/vyhladavanie-judikatov',
    },
    '/vyhladavanie-zakonov': {
      en: '/legislation-search',
      sk: '/vyhladavanie-zakonov',
    },
    '/matching-clients-with-legal-professionals': {
      en: '/matching-clients-with-legal-professionals',
      sk: '/spajame-klientov-s-pravnymi-profesionalmi',
    },
    '/client-intake': {
      en: '/client-intake',
      sk: '/priprava-pripadu',
    },
    '/security': {
      en: '/security',
      sk: '/bezpecnost',
    },
    '/faq': {
      en: '/faq',
      sk: '/faq',
    },
    '/storage': {
      en: '/storage',
      sk: '/ulozisko',
    },
    // Advocate practice surface (Spis, #6324). Same slug in both locales —
    // "kancelária" is the brand term, kept untranslated.
    '/kancelaria': {
      en: '/kancelaria',
      sk: '/kancelaria',
    },
    // Practice sub-surfaces (Spisy/Klienti, #6324). Same slug in both locales —
    // the domain nouns (Spis/Klient) are kept untranslated per #6269.
    '/kancelaria/klienti': {
      en: '/kancelaria/klienti',
      sk: '/kancelaria/klienti',
    },
    '/kancelaria/spisy': {
      en: '/kancelaria/spisy',
      sk: '/kancelaria/spisy',
    },
    '/kancelaria/spisy/[id]': {
      en: '/kancelaria/spisy/[id]',
      sk: '/kancelaria/spisy/[id]',
    },
    '/document-analysis': {
      en: '/document-analysis',
      sk: '/analyza-dokumentu',
    },
    '/document-analysis/[sessionId]': {
      en: '/document-analysis/[sessionId]',
      sk: '/analyza-dokumentu/[sessionId]',
    },
    '/generovanie': {
      en: '/generovanie',
      sk: '/generovanie',
    },
    '/generovanie/[sessionId]': {
      en: '/generovanie/[sessionId]',
      sk: '/generovanie/[sessionId]',
    },
    '/organization': {
      en: '/organization',
      sk: '/organizacia',
    },
    '/organization/general': {
      en: '/organization/general',
      sk: '/organizacia/vseobecne',
    },
    '/organization/members': {
      en: '/organization/members',
      sk: '/organizacia/clenovia',
    },
    '/organization/groups': {
      en: '/organization/groups',
      sk: '/organizacia/skupiny',
    },
    '/organization/billing': {
      en: '/organization/billing',
      sk: '/organizacia/fakturacia',
    },
    '/organization/usage': {
      en: '/organization/usage',
      sk: '/organizacia/pouzitie',
    },
    '/organization/security': {
      en: '/organization/security',
      sk: '/organizacia/bezpecnost',
    },
    '/organization/activity': {
      en: '/organization/activity',
      sk: '/organizacia/aktivita',
    },
    '/organization/legal': {
      en: '/organization/legal',
      sk: '/organizacia/pravne',
    },
    '/checkout/[params]': {
      en: '/checkout/[params]',
      sk: '/checkout/[params]',
    },
    '/pricing/pack-success': {
      en: '/pricing/pack-success',
      sk: '/cennik/pack-success',
    },
    // Public Lexomat blog. Pluralized (blogy/blogs) because the index lists
    // many posts. Route folder stays `blog`; `href="/blog"` keys throughout
    // the codebase auto-localise to these URLs via next-intl. Advocate/pro
    // blog surfaces are deliberately NOT renamed (bounded SEO risk).
    '/blog': {
      en: '/blogs',
      sk: '/blogy',
    },
    '/blog/[slug]': {
      en: '/blogs/[slug]',
      sk: '/blogy/[slug]',
    },
    // Poradňa topic library — public, SEO-indexed legal Q&A knowledge base.
    '/poradna': {
      en: '/poradna',
      sk: '/poradna',
    },
    '/poradna/[slug]': {
      en: '/poradna/[slug]',
      sk: '/poradna/[slug]',
    },
    '/authors/[slug]': {
      en: '/authors/[slug]',
      sk: '/autori/[slug]',
    },
    // Unified inbox: one route, both viewer sides. Lives in the app shell
    // (alongside /chat, /storage, /search) so an inquiry feels like a
    // first-class app surface, not a buried settings tab. The page
    // derives viewerSide from the match row vs the auth user — same URL
    // works whether you sent the inquiry or received it.
    '/inbox': {
      en: '/inbox',
      sk: '/dopyty',
    },
    '/inbox/[matchId]': {
      en: '/inbox/[matchId]',
      sk: '/dopyty/[matchId]',
    },
    // Client's shared-items inbox — "Zdieľané so mnou" (#6325 Slice 3).
    // Kept as /zdielane in both locales (brand term; matches kancelaria pattern
    // of not translating Spis/Klient domain nouns).
    '/zdielane': {
      en: '/zdielane',
      sk: '/zdielane',
    },
    '/mfa/recover': {
      en: '/mfa/recover',
      sk: '/mfa/obnovit',
    },
    // /admin/* — operator-only surface (#6687). Same slug in both locales;
    // the surface is not user-facing marketing copy and is not linked from
    // any public page. Type-safe Link/redirect support is required because
    // admin/layout.tsx + admin/page.tsx both reference these paths.
    '/admin': {
      en: '/admin',
      sk: '/admin',
    },
    '/admin/blog/attestations': {
      en: '/admin/blog/attestations',
      sk: '/admin/blog/attestations',
    },
  },
});

export type Locale = (typeof routing.locales)[number];

export type Pathname =
  | '/'
  | '/about'
  | '/why-lexomat'
  | '/chunk-viewer'
  | '/chat'
  | '/chats'
  | '/contact'
  | '/document-analysis'
  | '/document-analysis/[sessionId]'
  | '/faq'
  | '/feedback'
  | '/storage'
  | '/kancelaria'
  | '/kancelaria/klienti'
  | '/kancelaria/spisy'
  | '/kancelaria/spisy/[id]'
  | '/privacy-policy'
  | '/research'
  | '/results'
  | '/search'
  | '/search/[sessionId]'
  | '/today'
  | '/security'
  | '/terms-of-use'
  | '/processing-terms'
  | '/dpa/sub-processors'
  | '/professional-directory'
  | '/professional-directory/claim/[id]'
  | '/professional-directory/claim/verify'
  | '/professional-directory/dashboard'
  | '/professional-directory/opt-out'
  | '/professional-directory/opt-out/verify'
  | '/chat/[sessionId]'
  | '/chat/share/[sessionId]'
  | '/generovanie'
  | '/generovanie/[sessionId]'
  | '/document/[...segments]'
  | '/pricing'
  | '/manage'
  | '/cancelling-subscription'
  | '/confirm-subscription'
  | '/user-profile'
  | '/user-profile/history'
  | '/user-profile/subscription'
  | '/user-profile/professional/subscription'
  | '/user-profile/invoice'
  | '/user-profile/billing'
  | '/user-profile/user-type'
  | '/user-profile/appearance'
  | '/user-profile/email-preferences'
  | '/user-profile/preferences'
  | '/user-profile/organizations'
  | '/user-profile/security'
  | '/user-profile/usage'
  | '/user-profile/ai-connections'
  | '/organization'
  | '/organization/general'
  | '/organization/members'
  | '/organization/groups'
  | '/organization/billing'
  | '/organization/usage'
  | '/organization/security'
  | '/organization/activity'
  | '/organization/legal'
  | '/checkout/[params]'
  | '/pricing/pack-success'
  | '/blog'
  | '/blog/[slug]'
  | '/authors/[slug]'
  | '/sign-in'
  | '/for-professionals'
  | '/for-lawyers'
  | '/spisy-zdarma'
  | '/mcp'
  | '/doc'
  | '/doc/[slug]'
  | '/sign-up'
  | '/inbox'
  | '/inbox/[matchId]'
  | '/zdielane'
  | '/mfa/recover'
  | '/client-intake'
  | '/advokati'
  | '/advokati/[id]'
  | '/advokati/[id]/blog'
  | '/advokati/[id]/blog/[slug]'
  | '/oblast-prava'
  | '/oblast-prava/[category]'
  | '/mesto/[city]'
  | '/mesto/[city]/[category]'
  | '/pravna-resers-online'
  | '/ai-pre-pravnikov'
  | '/vyhladavanie-judikatov'
  | '/vyhladavanie-zakonov'
  | '/admin'
  | '/admin/blog/attestations';
// Lightweight wrappers around Next.js' navigation APIs
// that will consider the routing configuration
export const { Link, redirect, usePathname, useRouter, getPathname } =
  createNavigation(routing);
